import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Tag, Trash2, Copy, Share2, Plus, Palette } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { api, fmtErr, API } from "../../lib/api";

const DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

export default function SettingsTab({ restaurant, onSaved }) {
  const [profile, setProfile] = useState({
    name: restaurant.name, description: restaurant.description || "", cuisine: restaurant.cuisine || "",
    address: restaurant.address || "", city: restaurant.city || "", phone: restaurant.phone || "",
    image_url: restaurant.image_url || "", slug: restaurant.slug || "", timezone: restaurant.timezone || "Europe/London",
    listed: restaurant.listed !== false,
    voucher_url: restaurant.voucher_url || "",
    webhook_url: restaurant.webhook_url || "",
  });
  const [hours, setHours] = useState(restaurant.operating_hours || {});
  const [photosText, setPhotosText] = useState((restaurant.photos || []).join("\n"));
  const [offers, setOffers] = useState([]);
  const [offerForm, setOfferForm] = useState({ title: "", description: "", valid_until: "" });
  const [stripeKey, setStripeKey] = useState("");
  const [deposit, setDeposit] = useState({
    require_deposit: restaurant.require_deposit || false,
    deposit_amount: restaurant.deposit_amount || 10,
    deposit_on_combo: restaurant.deposit_on_combo || false,
  });
  const [onlineCfg, setOnlineCfg] = useState({
    enabled: restaurant.online_ordering?.enabled || false,
    delivery: restaurant.online_ordering?.delivery || false,
  });
  const [testingSmtp, setTestingSmtp] = useState(false);
  const [emailCfg, setEmailCfg] = useState({
    notify_on_booking: restaurant.notify_on_booking !== false,
    notify_email: restaurant.notify_email || "",
    smtp_enabled: restaurant.smtp?.enabled || false,
    smtp_host: restaurant.smtp?.host || "",
    smtp_port: restaurant.smtp?.port || 587,
    smtp_username: restaurant.smtp?.username || "",
    smtp_password: "",
    smtp_from: restaurant.smtp?.from_email || "",
  });
  const [widgetCfg, setWidgetCfg] = useState({
    require_phone: true,
    require_dietary: restaurant.widget_form?.require_dietary || false,
    require_notes: restaurant.widget_form?.require_notes || false,
    show_occasion: restaurant.widget_form?.show_occasion !== false,
  });
  const [smsRem, setSmsRem] = useState(restaurant.sms_reminders || false);
  const [twilioCfg, setTwilioCfg] = useState({
    enabled: restaurant.twilio?.enabled || false,
    account_sid: restaurant.twilio?.account_sid || "",
    auth_token: "",
    from_number: restaurant.twilio?.from_number || "",
    whatsapp: restaurant.twilio?.whatsapp || false,
  });
  const [apiKeys, setApiKeys] = useState([]);
  const [newKeyName, setNewKeyName] = useState("");
  const [createdKey, setCreatedKey] = useState(null);
  const [brand, setBrand] = useState({
    logo_url: restaurant.logo_url || "",
    header_image_url: restaurant.header_image_url || "",
    photos: restaurant.photos || [],
  });
  const [imgUploading, setImgUploading] = useState("");
  const [galleryUrl, setGalleryUrl] = useState("");

  const uploadImage = async (kind, file, done) => {
    const fd = new FormData();
    fd.append("file", file);
    setImgUploading(kind);
    try {
      const { data } = await api.post(`/restaurants/${restaurant.id}/media?kind=${kind}`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      done(`${API}${data.url}`);
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(fmtErr(e));
    }
    setImgUploading("");
  };

  const loadKeys = () => api.get(`/restaurants/${restaurant.id}/api-keys`).then((r) => setApiKeys(r.data)).catch(() => {});
  useEffect(() => { loadKeys(); }, [restaurant.id]);

  const createKey = async () => {
    try {
      const { data } = await api.post(`/restaurants/${restaurant.id}/api-keys`, { name: newKeyName });
      setCreatedKey(data.key);
      setNewKeyName("");
      loadKeys();
      toast.success("API key created — copy it now, it won't be shown again");
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const deleteKey = async (kid) => {
    try {
      await api.delete(`/restaurants/${restaurant.id}/api-keys/${kid}`);
      toast.success("API key revoked");
      loadKeys();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };
  const [questions, setQuestions] = useState(restaurant.custom_questions || []);

  const updateQ = (i, patch) => setQuestions(questions.map((q, j) => (j === i ? { ...q, ...patch } : q)));

  const testSmtp = async () => {
    setTestingSmtp(true);
    try {
      await api.post(`/restaurants/${restaurant.id}/smtp-test`, {});
      toast.success("Test email sent — check your inbox");
    } catch (e) {
      toast.error(fmtErr(e));
    }
    setTestingSmtp(false);
  };
  const [servicesList, setServicesList] = useState(restaurant.services || []);
  const [policy, setPolicy] = useState({
    cancellation_hours: restaurant.booking_policy?.cancellation_hours ?? 24,
    max_party_size: restaurant.booking_policy?.max_party_size ?? 12,
    slot_duration: restaurant.booking_policy?.slot_duration ?? 90,
    turnover_minutes: restaurant.booking_policy?.turnover_minutes ?? 15,
    large_party_threshold: restaurant.booking_policy?.large_party_threshold ?? 0,
    large_party_duration: restaurant.booking_policy?.large_party_duration ?? 0,
    large_party_turnover: restaurant.booking_policy?.large_party_turnover ?? 0,
    closed_dates: (restaurant.booking_policy?.closed_dates || []).join("\n"),
  });
  const [calColors, setCalColors] = useState({
    statuses: {
      confirmed: "#3B82F6", seated: "#3B82F6", completed: "#22C55E",
      no_show: "#EF4444", pending: "#F59E0B", walkin: "#D97706",
      ...(restaurant.calendar_colors?.statuses || {}),
    },
    occasions: restaurant.calendar_colors?.occasions || [],
  });
  const [saving, setSaving] = useState(false);

  const loadOffers = () => api.get(`/restaurants/${restaurant.id}/offers`).then((r) => setOffers(r.data)).catch(() => {});
  useEffect(() => { loadOffers(); }, [restaurant.id]);

  const addOffer = async () => {
    try {
      await api.post(`/restaurants/${restaurant.id}/offers`, offerForm);
      toast.success("Offer published");
      setOfferForm({ title: "", description: "", valid_until: "" });
      loadOffers();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const removeOffer = async (oid) => {
    try {
      await api.delete(`/restaurants/${restaurant.id}/offers/${oid}`);
      toast.success("Offer removed");
      loadOffers();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const disconnectStripe = async () => {
    try {
      await api.put(`/restaurants/${restaurant.id}`, { stripe_secret_key: "" });
      toast.success("Stripe disconnected");
      setStripeKey("");
      onSaved();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/restaurants/${restaurant.id}`, {
        ...profile,
        photos: photosText.split("\n").map((s) => s.trim()).filter(Boolean),
        require_deposit: deposit.require_deposit,
        deposit_amount: Number(deposit.deposit_amount),
        deposit_on_combo: deposit.deposit_on_combo,
        online_ordering: onlineCfg,
        notify_on_booking: emailCfg.notify_on_booking,
        notify_email: emailCfg.notify_email,
        smtp: {
          enabled: emailCfg.smtp_enabled, host: emailCfg.smtp_host, port: Number(emailCfg.smtp_port) || 587,
          username: emailCfg.smtp_username, from_email: emailCfg.smtp_from,
          ...(emailCfg.smtp_password ? { password: emailCfg.smtp_password } : {}),
        },
        widget_form: widgetCfg,
        sms_reminders: smsRem,
        twilio: {
          enabled: twilioCfg.enabled, account_sid: twilioCfg.account_sid, from_number: twilioCfg.from_number,
          whatsapp: twilioCfg.whatsapp,
          ...(twilioCfg.auth_token ? { auth_token: twilioCfg.auth_token } : {}),
        },
        custom_questions: questions.filter((q) => q.label.trim()).map((q) => ({
          ...q, options: q.type === "choice" ? (typeof q.options === "string" ? q.options.split(",").map((o) => o.trim()).filter(Boolean) : q.options) : [],
        })),
        logo_url: brand.logo_url,
        header_image_url: brand.header_image_url,
        photos: brand.photos,
        services: servicesList,
        ...(stripeKey ? { stripe_secret_key: stripeKey } : {}),
        operating_hours: hours,
        booking_policy: {
          cancellation_hours: Number(policy.cancellation_hours),
          max_party_size: Number(policy.max_party_size),
          slot_duration: Number(policy.slot_duration),
          turnover_minutes: Number(policy.turnover_minutes),
          large_party_threshold: Number(policy.large_party_threshold) || 0,
          large_party_duration: Number(policy.large_party_duration) || 0,
          large_party_turnover: Number(policy.large_party_turnover) || 0,
          closed_dates: (policy.closed_dates || "").split("\n").map((s) => s.trim()).filter(Boolean),
        },
        calendar_colors: calColors,
      });
      toast.success("Settings saved");
      onSaved();
    } catch (e) {
      toast.error(fmtErr(e));
    }
    setSaving(false);
  };

  return (
    <div data-testid="settings-tab" className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      <div className="rounded-2xl border border-border/60 bg-card p-6 space-y-4">
        <h3 className="font-outfit font-medium text-lg">Profile</h3>
        {[["name", "Name"], ["cuisine", "Cuisine"], ["address", "Address"], ["city", "City"], ["phone", "Phone"], ["image_url", "Image URL"]].map(([key, label]) => (
          <div key={key}>
            <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">{label}</Label>
            <Input data-testid={`setting-${key}-input`} value={profile[key]} onChange={(e) => setProfile({ ...profile, [key]: e.target.value })}
              className="mt-1.5 bg-background border-border/60" />
          </div>
        ))}
        <div>
          <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Timezone</Label>
          <Select value={profile.timezone} onValueChange={(v) => setProfile({ ...profile, timezone: v })}>
            <SelectTrigger data-testid="setting-timezone-select" className="mt-1.5 bg-background border-border/60">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {["Europe/London", "Europe/Dublin", "Europe/Paris", "Europe/Berlin", "Europe/Madrid", "Europe/Rome",
                "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
                "Asia/Dubai", "Asia/Singapore", "Asia/Hong_Kong", "Australia/Sydney", "Pacific/Auckland"].map((tz) => (
                <SelectItem key={tz} value={tz}>{tz}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground mt-1.5">Booking slots, reminders and cancellation windows use this timezone.</p>
        </div>
        <div>
          <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Description</Label>
          <Textarea data-testid="setting-description-input" rows={3} value={profile.description}
            onChange={(e) => setProfile({ ...profile, description: e.target.value })} className="mt-1.5 bg-background border-border/60" />
        </div>
        <div>
          <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Photo gallery URLs (one per line)</Label>
          <Textarea data-testid="setting-photos-input" rows={3} value={photosText}
            onChange={(e) => setPhotosText(e.target.value)} className="mt-1.5 bg-background border-border/60"
            placeholder="https://..." />
        </div>
        <div className="flex items-center justify-between gap-4 rounded-xl border border-border/40 px-4 py-3">
          <div>
            <Label className="text-sm">Show on marketplace</Label>
            <p className="text-xs text-muted-foreground mt-0.5">Turn off to hide your venue from the home page and restaurant search. Guests can still book via your direct link and widget.</p>
          </div>
          <Switch data-testid="setting-listed-switch" checked={profile.listed}
            onCheckedChange={(v) => setProfile({ ...profile, listed: v })} />
        </div>
      </div>

      <div className="space-y-8">
        <div className="rounded-2xl border border-border/60 bg-card p-6">
          <h3 className="font-outfit font-medium text-lg">Operating hours</h3>
          <div className="mt-4 space-y-3">
            {DAYS.map((d) => {
              const h = hours[d] || { open: "12:00", close: "23:00", closed: false };
              return (
                <div key={d} className="flex items-center gap-3">
                  <span className="text-xs tracking-[0.15em] uppercase text-muted-foreground w-10">{d}</span>
                  <Switch data-testid={`hours-${d}-switch`} checked={!h.closed}
                    onCheckedChange={(v) => setHours({ ...hours, [d]: { ...h, closed: !v } })} />
                  {!h.closed && (
                    <>
                      <Input data-testid={`hours-${d}-open`} type="time" value={h.open}
                        onChange={(e) => setHours({ ...hours, [d]: { ...h, open: e.target.value } })}
                        className="bg-background border-border/60 h-8 w-28" />
                      <span className="text-muted-foreground text-xs">–</span>
                      <Input data-testid={`hours-${d}-close`} type="time" value={h.close}
                        onChange={(e) => setHours({ ...hours, [d]: { ...h, close: e.target.value } })}
                        className="bg-background border-border/60 h-8 w-28" />
                    </>
                  )}
                  {h.closed && <span className="text-xs text-muted-foreground">Closed</span>}
                </div>
              );
            })}
          </div>
        </div>

        <div className="rounded-2xl border border-border/60 bg-card p-6">
          <h3 className="font-outfit font-medium text-lg">Booking policy</h3>
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Cancellation window (h)</Label>
              <Input data-testid="policy-cancellation-input" type="number" min="0" value={policy.cancellation_hours}
                onChange={(e) => setPolicy({ ...policy, cancellation_hours: e.target.value })} className="mt-1.5 bg-background border-border/60" />
            </div>
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Max party size</Label>
              <Input data-testid="policy-party-input" type="number" min="1" value={policy.max_party_size}
                onChange={(e) => setPolicy({ ...policy, max_party_size: e.target.value })} className="mt-1.5 bg-background border-border/60" />
            </div>
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Dining duration (min)</Label>
              <Input data-testid="policy-duration-input" type="number" min="30" step="15" value={policy.slot_duration}
                onChange={(e) => setPolicy({ ...policy, slot_duration: e.target.value })} className="mt-1.5 bg-background border-border/60" />
            </div>
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Turnover buffer (min)</Label>
              <Input data-testid="policy-turnover-input" type="number" min="0" step="5" value={policy.turnover_minutes}
                onChange={(e) => setPolicy({ ...policy, turnover_minutes: e.target.value })} className="mt-1.5 bg-background border-border/60" />
            </div>
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Large party from (covers)</Label>
              <Input data-testid="policy-large-threshold-input" type="number" min="0" value={policy.large_party_threshold}
                onChange={(e) => setPolicy({ ...policy, large_party_threshold: e.target.value })} className="mt-1.5 bg-background border-border/60" />
            </div>
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Large party duration (min)</Label>
              <Input data-testid="policy-large-duration-input" type="number" min="0" step="15" value={policy.large_party_duration}
                onChange={(e) => setPolicy({ ...policy, large_party_duration: e.target.value })} className="mt-1.5 bg-background border-border/60" />
            </div>
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Large party turnover (min)</Label>
              <Input data-testid="policy-large-turnover-input" type="number" min="0" step="5" value={policy.large_party_turnover}
                onChange={(e) => setPolicy({ ...policy, large_party_turnover: e.target.value })} className="mt-1.5 bg-background border-border/60" />
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-3">Large-party rules apply once a booking reaches the threshold — set threshold to 0 to disable. Large tables get their own dining duration and turnover buffer automatically.</p>
          <div className="mt-3">
            <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Close-out dates (one per line)</Label>
            <Textarea data-testid="policy-closed-dates-input" rows={2} value={policy.closed_dates}
              onChange={(e) => setPolicy({ ...policy, closed_dates: e.target.value })}
              placeholder={"2026-12-25\n2026-12-26"} className="mt-1.5 bg-background border-border/60" />
            <p className="text-xs text-muted-foreground mt-1.5">Online bookings are blocked on these dates (e.g. private events, holidays). You can still add bookings manually.</p>
          </div>
        </div>

        <div className="rounded-2xl border border-border/60 bg-card p-6" data-testid="calendar-colors-card">
          <h3 className="font-outfit font-medium text-lg flex items-center gap-2"><Palette className="w-4 h-4 text-primary" /> Calendar colours</h3>
          <p className="text-xs text-muted-foreground mt-1">Colours for booking blocks on the calendar. Occasion rules override the status colour when the occasion text contains the keyword — so birthdays pop before the party even arrives.</p>
          <div className="grid grid-cols-2 gap-3 mt-4">
            {[["confirmed", "Booked (default)"], ["seated", "Seated"], ["completed", "Completed"], ["no_show", "No show"], ["pending", "Pending"], ["walkin", "Walk-in"]].map(([k, label]) => (
              <div key={k} className="flex items-center gap-3">
                <input data-testid={`color-${k}`} type="color" value={calColors.statuses[k] || "#3B82F6"}
                  onChange={(e) => setCalColors({ ...calColors, statuses: { ...calColors.statuses, [k]: e.target.value } })}
                  className="w-9 h-9 rounded-lg border border-border/60 bg-background cursor-pointer" />
                <div>
                  <p className="text-sm">{label}</p>
                  <p className="text-[10px] text-muted-foreground uppercase">{(calColors.statuses[k] || "").toUpperCase()}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-5">
            <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Occasion colours</Label>
            <div className="space-y-2 mt-2">
              {calColors.occasions.map((o, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`occasion-rule-${i}`}>
                  <Input value={o.match} placeholder="Keyword, e.g. birthday" data-testid={`occasion-rule-match-${i}`}
                    onChange={(e) => setCalColors({ ...calColors, occasions: calColors.occasions.map((x, j) => j === i ? { ...x, match: e.target.value } : x) })}
                    className="bg-background border-border/60 flex-1" />
                  <input type="color" value={o.color} data-testid={`occasion-rule-color-${i}`}
                    onChange={(e) => setCalColors({ ...calColors, occasions: calColors.occasions.map((x, j) => j === i ? { ...x, color: e.target.value } : x) })}
                    className="w-9 h-9 rounded-lg border border-border/60 bg-background cursor-pointer" />
                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0 rounded-full" data-testid={`occasion-rule-remove-${i}`}
                    onClick={() => setCalColors({ ...calColors, occasions: calColors.occasions.filter((_, j) => j !== i) })}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
              {calColors.occasions.length < 12 && (
                <Button size="sm" variant="outline" className="rounded-full" data-testid="occasion-rule-add"
                  onClick={() => setCalColors({ ...calColors, occasions: [...calColors.occasions, { match: "", color: "#D1567A" }] })}>
                  <Plus className="w-4 h-4 mr-2" /> Add occasion rule
                </Button>
              )}
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-border/60 bg-card p-6">
          <h3 className="font-outfit font-medium text-lg">Service periods</h3>
          <p className="text-xs text-muted-foreground mt-1">
            Optional. Split the day into services (e.g. Lunch / Dinner) with their own hours, cover limits and per-slot pacing. If empty, your operating hours above are used.
          </p>
          {servicesList.map((s, idx) => (
            <div key={idx} data-testid={`service-row-${idx}`} className="rounded-xl border border-border/40 p-4 mt-4 space-y-3">
              <div className="flex items-center gap-3">
                <Input data-testid={`service-name-${idx}`} placeholder="Service name (e.g. Lunch)" value={s.name}
                  onChange={(e) => setServicesList(servicesList.map((x, i) => (i === idx ? { ...x, name: e.target.value } : x)))}
                  className="bg-background border-border/60" />
                <div className="flex items-center gap-2 shrink-0">
                  <Switch data-testid={`service-enabled-${idx}`} checked={s.enabled !== false}
                    onCheckedChange={(v) => setServicesList(servicesList.map((x, i) => (i === idx ? { ...x, enabled: v } : x)))} />
                  <button data-testid={`service-remove-${idx}`} onClick={() => setServicesList(servicesList.filter((_, i) => i !== idx))}
                    className="text-muted-foreground hover:text-destructive transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Input data-testid={`service-open-${idx}`} type="time" value={s.open || "12:00"}
                  onChange={(e) => setServicesList(servicesList.map((x, i) => (i === idx ? { ...x, open: e.target.value } : x)))}
                  className="bg-background border-border/60 h-9 w-28" />
                <span className="text-muted-foreground text-xs">–</span>
                <Input data-testid={`service-close-${idx}`} type="time" value={s.close || "15:00"}
                  onChange={(e) => setServicesList(servicesList.map((x, i) => (i === idx ? { ...x, close: e.target.value } : x)))}
                  className="bg-background border-border/60 h-9 w-28" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Max covers (0 = unlimited)</Label>
                  <Input data-testid={`service-covers-${idx}`} type="number" min="0" value={s.max_covers || 0}
                    onChange={(e) => setServicesList(servicesList.map((x, i) => (i === idx ? { ...x, max_covers: Number(e.target.value) } : x)))}
                    className="mt-1.5 bg-background border-border/60" />
                </div>
                <div>
                  <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Max bookings / slot</Label>
                  <Input data-testid={`service-pacing-${idx}`} type="number" min="0" value={s.max_per_slot || 0}
                    onChange={(e) => setServicesList(servicesList.map((x, i) => (i === idx ? { ...x, max_per_slot: Number(e.target.value) } : x)))}
                    className="mt-1.5 bg-background border-border/60" />
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {DAYS.map((d) => {
                  const on = !s.days || s.days.length === 0 || s.days.includes(d);
                  return (
                    <button key={d} type="button" data-testid={`service-day-${idx}-${d}`}
                      onClick={() => setServicesList(servicesList.map((x, i) => {
                        if (i !== idx) return x;
                        const days = x.days && x.days.length ? [...x.days] : [...DAYS];
                        const next = days.includes(d) ? days.filter((y) => y !== d) : [...days, d];
                        return { ...x, days: next.length === DAYS.length ? [] : next };
                      }))}
                      className={`rounded-full border px-2.5 py-1 text-[10px] uppercase tracking-wider transition-colors ${
                        on ? "border-primary/60 bg-primary/10 text-primary" : "border-border/60 text-muted-foreground"
                      }`}>
                      {d}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
          <Button data-testid="add-service-btn" variant="outline" size="sm" className="rounded-full mt-4"
            onClick={() => setServicesList([...servicesList, { name: "", open: "12:00", close: "15:00", max_covers: 0, max_per_slot: 0, enabled: true, days: [] }])}>
            Add service period
          </Button>
        </div>

        <div className="rounded-2xl border border-border/60 bg-card p-6">
          <h3 className="font-outfit font-medium text-lg flex items-center gap-2"><Share2 className="w-5 h-5 text-primary" /> Share & embed</h3>
          <div className="mt-4 space-y-4">
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Friendly URL slug</Label>
              <div className="flex items-center gap-2 mt-1.5">
                <span className="text-xs text-muted-foreground shrink-0">/r/</span>
                <Input data-testid="setting-slug-input" value={profile.slug}
                  onChange={(e) => setProfile({ ...profile, slug: e.target.value })}
                  className="bg-background border-border/60" />
              </div>
              <p className="text-xs text-muted-foreground mt-1.5">
                Your public page: <span className="text-foreground">{window.location.origin}/r/{profile.slug || "..."}</span> — saved with "Save settings".
              </p>
            </div>
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Embed the booking widget on your own site</Label>
              <div className="mt-1.5 rounded-xl border border-border/40 bg-background p-3 text-xs font-mono text-muted-foreground break-all" data-testid="embed-snippet">
                {`<iframe id="td-widget" src="${window.location.origin}/embed/${restaurant.slug || restaurant.id}" width="100%" height="480" frameborder="0" style="border:0;border-radius:16px;overflow:hidden" title="Book a table"></iframe>\n<script>window.addEventListener("message",function(e){if(e.data&&e.data.type==="tablediary:height"){var f=document.getElementById("td-widget");if(f)f.style.height=Math.min(e.data.height+24,1200)+"px";}});</script>`}
              </div>
              <p className="text-xs text-muted-foreground mt-1.5">The companion script auto-resizes the frame as guests move through the booking steps — no clipping or inner scrolling.</p>
              <Button data-testid="copy-embed-btn" size="sm" variant="outline" className="rounded-full mt-2"
                onClick={() => {
                  navigator.clipboard.writeText(`<iframe id="td-widget" src="${window.location.origin}/embed/${restaurant.slug || restaurant.id}" width="100%" height="480" frameborder="0" style="border:0;border-radius:16px;overflow:hidden" title="Book a table"></iframe>\n<script>window.addEventListener("message",function(e){if(e.data&&e.data.type==="tablediary:height"){var f=document.getElementById("td-widget");if(f)f.style.height=Math.min(e.data.height+24,1200)+"px";}});</script>`);
                  toast.success("Embed code copied");
                }}>
                <Copy className="w-3.5 h-3.5 mr-2" /> Copy embed code
              </Button>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-border/60 bg-card p-6">
          <h3 className="font-outfit font-medium text-lg flex items-center gap-2"><Tag className="w-5 h-5 text-primary" /> Special offers</h3>
          <p className="text-xs text-muted-foreground mt-1">Published offers appear on your public page and the marketplace.</p>
          <div className="mt-4 space-y-3">
            {offers.map((o) => (
              <div key={o.id} data-testid={`offer-row-${o.id}`} className="flex items-start justify-between rounded-xl border border-border/40 px-4 py-3">
                <div>
                  <p className="text-sm font-medium">{o.title}</p>
                  <p className="text-xs text-muted-foreground">{o.description}</p>
                </div>
                <button data-testid={`delete-offer-${o.id}`} onClick={() => removeOffer(o.id)}
                  className="text-muted-foreground hover:text-destructive transition-colors shrink-0 ml-3">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            {offers.length === 0 && <p className="text-sm text-muted-foreground" data-testid="no-offers-msg">No active offers.</p>}
          </div>
          <div className="mt-5 space-y-3">
            <Input data-testid="offer-title-input" placeholder="Offer title (e.g. 2-for-1 Tuesdays)" value={offerForm.title}
              onChange={(e) => setOfferForm({ ...offerForm, title: e.target.value })} className="bg-background border-border/60" />
            <Textarea data-testid="offer-description-input" rows={2} placeholder="Details..." value={offerForm.description}
              onChange={(e) => setOfferForm({ ...offerForm, description: e.target.value })} className="bg-background border-border/60" />
            <div className="flex gap-3">
              <Input data-testid="offer-valid-until-input" type="date" value={offerForm.valid_until}
                onChange={(e) => setOfferForm({ ...offerForm, valid_until: e.target.value })} className="bg-background border-border/60 max-w-[180px]" />
              <Button data-testid="offer-publish-btn" onClick={addOffer} disabled={!offerForm.title} className="rounded-full">Publish offer</Button>
            </div>
          </div>
        </div>
        <div className="rounded-2xl border border-border/60 bg-card p-6">
          <h3 className="font-outfit font-medium text-lg">Deposits via your own Stripe</h3>
          <p className="text-xs text-muted-foreground mt-1">
            Optional. Connect your own Stripe account to charge diners a deposit when they book. Funds go directly to your Stripe account — TableDiary never touches them.
          </p>
          <div className="mt-4 space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-sm">Require deposit to book</Label>
              <Switch data-testid="deposit-switch" checked={deposit.require_deposit}
                onCheckedChange={(v) => setDeposit({ ...deposit, require_deposit: v })} />
            </div>
            <div className="flex items-center justify-between mt-4">
              <div>
                <Label className="text-sm">Require deposit for combined-table bookings</Label>
                <p className="text-xs text-muted-foreground mt-0.5">Auto-charge the deposit only when a party needs tables joined together — couples book free.</p>
              </div>
              <Switch data-testid="deposit-combo-switch" checked={deposit.deposit_on_combo}
                onCheckedChange={(v) => setDeposit({ ...deposit, deposit_on_combo: v })} />
            </div>
            {(deposit.require_deposit || deposit.deposit_on_combo) && (
              <>
                <div>
                  <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Deposit amount (£ per booking)</Label>
                  <Input data-testid="deposit-amount-input" type="number" min="1" value={deposit.deposit_amount}
                    onChange={(e) => setDeposit({ ...deposit, deposit_amount: e.target.value })}
                    className="mt-1.5 bg-background border-border/60 max-w-[160px]" />
                </div>
                <div>
                  <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">
                    Your Stripe secret key {restaurant.stripe_connected && <span className="text-emerald-500 normal-case">(connected — enter a new key to replace)</span>}
                  </Label>
                  <Input data-testid="stripe-key-input" type="password" value={stripeKey} onChange={(e) => setStripeKey(e.target.value)}
                    placeholder="sk_live_... or sk_test_..." className="mt-1.5 bg-background border-border/60" />
                  <p className="text-xs text-muted-foreground mt-1.5">Find this in your Stripe Dashboard → Developers → API keys. Saved when you click "Save settings" below.</p>
                </div>
                {restaurant.stripe_connected && (
                  <Button data-testid="stripe-disconnect-btn" variant="ghost" size="sm" className="text-destructive" onClick={disconnectStripe}>
                    Disconnect Stripe
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
        <div className="rounded-2xl border border-border/60 bg-card p-6" data-testid="online-ordering-card">
          <h3 className="font-outfit font-medium text-lg">Online ordering</h3>
          <p className="text-xs text-muted-foreground mt-1">
            Let guests order collection or delivery from your menus at <span className="text-foreground">/order/{restaurant.slug || restaurant.id}</span>. Card payments use your connected Stripe; without one, orders are pay-on-collection.
          </p>
          <div className="mt-4 space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-sm">Enable online ordering</Label>
              <Switch data-testid="ordering-enabled-switch" checked={onlineCfg.enabled}
                onCheckedChange={(v) => setOnlineCfg({ ...onlineCfg, enabled: v })} />
            </div>
            {onlineCfg.enabled && (
              <div className="flex items-center justify-between">
                <Label className="text-sm">Offer delivery (as well as collection)</Label>
                <Switch data-testid="ordering-delivery-switch" checked={onlineCfg.delivery}
                  onCheckedChange={(v) => setOnlineCfg({ ...onlineCfg, delivery: v })} />
              </div>
            )}
          </div>
        </div>
        {restaurant.demo_data && (
          <div className="rounded-2xl border border-amber-500/40 bg-amber-500/5 p-6" data-testid="demo-data-card">
            <h3 className="font-outfit font-medium text-lg">Demo data</h3>
            <p className="text-xs text-muted-foreground mt-1.5">This venue was pre-filled with sample bookings so you can explore TableDiary. Remove them whenever you're ready to go live.</p>
            <Button data-testid="remove-demo-data-btn" variant="outline" className="mt-4 rounded-full border-amber-500/40"
              onClick={async () => {
                try {
                  const { data } = await api.delete(`/restaurants/${restaurant.id}/demo-data`);
                  toast.success(`Removed ${data.deleted} demo bookings`);
                  if (onSaved) onSaved();
                } catch (e) {
                  toast.error(fmtErr(e));
                }
              }}>
              Remove demo data
            </Button>
          </div>
        )}
        <div className="rounded-2xl border border-border/60 bg-card p-6" data-testid="booking-channels-card">
          <h3 className="font-outfit font-medium text-lg">Booking channels</h3>
          <p className="text-xs text-muted-foreground mt-1.5">Share these links on each platform — bookings made through them are tagged by source in your Reports.</p>
          <div className="mt-4 space-y-2.5">
            {[["google", "Google (Reserve with Google link)"], ["instagram", "Instagram"], ["facebook", "Facebook"], ["tripadvisor", "TripAdvisor"]].map(([ch, label]) => {
              const url = `${window.location.origin}/restaurants/${restaurant.slug || restaurant.id}?src=${ch}`;
              return (
                <div key={ch} className="flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm">{label}</p>
                    <p data-testid={`channel-url-${ch}`} className="text-xs text-muted-foreground truncate">{url}</p>
                  </div>
                  <Button data-testid={`channel-copy-${ch}`} size="sm" variant="outline" className="rounded-full h-8 text-xs shrink-0"
                    onClick={() => { navigator.clipboard.writeText(url); toast.success(`${label} link copied`); }}>
                    Copy
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
        <div className="rounded-2xl border border-border/60 bg-card p-6" data-testid="gift-vouchers-card">
          <h3 className="font-outfit font-medium text-lg">Gift vouchers</h3>
          <div className="mt-3 rounded-xl border border-primary/40 bg-primary/5 p-4">
            <p className="text-sm"><span className="font-medium text-primary">Recommended: GiftVouchers.app</span> — our preferred gift voucher partner.</p>
            <p className="text-xs text-muted-foreground mt-1.5">
              Purpose-built for restaurants: instant email delivery, beautiful branded vouchers, balance tracking and easy redemption. Leave the link below empty and your guests go straight to GiftVouchers.app.
            </p>
            <a data-testid="giftvouchers-app-link" href="https://giftvouchers.app" target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline mt-2.5">
              Open GiftVouchers.app
            </a>
          </div>
          <div className="mt-4">
            <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Or link your own voucher program</Label>
            <p className="text-xs text-muted-foreground mt-1">Already selling vouchers elsewhere? Paste the page URL and we'll send guests there instead of GiftVouchers.app.</p>
            <Input data-testid="voucher-url-input" type="url" placeholder="https://your-voucher-page.example.com"
              value={profile.voucher_url} onChange={(e) => setProfile({ ...profile, voucher_url: e.target.value })}
              className="mt-1.5 bg-background border-border/60" />
          </div>
        </div>
        <div className="rounded-2xl border border-border/60 bg-card p-6" data-testid="email-notifications-card">
          <h3 className="font-outfit font-medium text-lg">Email &amp; notifications</h3>
          <p className="text-xs text-muted-foreground mt-1">Customers always receive booking confirmations. Choose whether your business gets a copy too.</p>
          <div className="mt-4 space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-sm">Email us about new bookings</Label>
              <Switch data-testid="notify-booking-switch" checked={emailCfg.notify_on_booking}
                onCheckedChange={(v) => setEmailCfg({ ...emailCfg, notify_on_booking: v })} />
            </div>
            {emailCfg.notify_on_booking && (
              <Input data-testid="notify-email-input" type="email" placeholder="Notification email (default: owner email)" value={emailCfg.notify_email}
                onChange={(e) => setEmailCfg({ ...emailCfg, notify_email: e.target.value })} className="bg-background border-border/60" />
            )}
          </div>
          <div className="mt-6 border-t border-border/40 pt-5">
            <div className="flex items-center justify-between">
              <Label className="text-sm">Send email via our own SMTP server</Label>
              <Switch data-testid="biz-smtp-enabled-switch" checked={emailCfg.smtp_enabled}
                onCheckedChange={(v) => setEmailCfg({ ...emailCfg, smtp_enabled: v })} />
            </div>
            {emailCfg.smtp_enabled && (
              <div className="mt-4 space-y-3">
                <div className="grid grid-cols-3 gap-3">
                  <Input data-testid="biz-smtp-host" placeholder="Host (e.g. smtp.mailgun.org)" value={emailCfg.smtp_host}
                    onChange={(e) => setEmailCfg({ ...emailCfg, smtp_host: e.target.value })} className="col-span-2 bg-background border-border/60" />
                  <Input data-testid="biz-smtp-port" type="number" placeholder="Port" value={emailCfg.smtp_port}
                    onChange={(e) => setEmailCfg({ ...emailCfg, smtp_port: e.target.value })} className="bg-background border-border/60" />
                </div>
                <Input data-testid="biz-smtp-username" placeholder="Username" value={emailCfg.smtp_username}
                  onChange={(e) => setEmailCfg({ ...emailCfg, smtp_username: e.target.value })} className="bg-background border-border/60" />
                <Input data-testid="biz-smtp-password" type="password"
                  placeholder={restaurant.smtp?.has_password ? "Password saved — enter to replace" : "Password"}
                  value={emailCfg.smtp_password} onChange={(e) => setEmailCfg({ ...emailCfg, smtp_password: e.target.value })}
                  className="bg-background border-border/60" />
                <Input data-testid="biz-smtp-from" type="email" placeholder="From email (e.g. bookings@yourvenue.com)" value={emailCfg.smtp_from}
                  onChange={(e) => setEmailCfg({ ...emailCfg, smtp_from: e.target.value })} className="bg-background border-border/60" />
                <p className="text-xs text-muted-foreground">Port 587 (STARTTLS) is standard; port 465 (SSL) is supported and used automatically as a fallback.</p>
                <Button data-testid="biz-smtp-test-btn" size="sm" variant="outline" className="rounded-full"
                  onClick={testSmtp} disabled={testingSmtp || !restaurant.smtp?.enabled}>
                  {testingSmtp ? "Sending..." : "Send test email"}
                </Button>
                {!restaurant.smtp?.enabled && <p className="text-[11px] text-muted-foreground">Uses your saved settings — save changes first, then test.</p>}
              </div>
            )}
          </div>
        </div>
        <div className="rounded-2xl border border-border/60 bg-card p-6" data-testid="booking-form-card">
          <h3 className="font-outfit font-medium text-lg">Booking form</h3>
          <p className="text-xs text-muted-foreground mt-1">
            Controls the booking form diners see — on your public page and the embeddable widget. Name, email and phone are always required.
          </p>
          <div className="mt-4 space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-sm">Require dietary / allergy info</Label>
              <Switch data-testid="form-require-dietary" checked={widgetCfg.require_dietary}
                onCheckedChange={(v) => setWidgetCfg({ ...widgetCfg, require_dietary: v })} />
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-sm">Require a note / special request</Label>
              <Switch data-testid="form-require-notes" checked={widgetCfg.require_notes}
                onCheckedChange={(v) => setWidgetCfg({ ...widgetCfg, require_notes: v })} />
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-sm">Show occasion dropdown</Label>
              <Switch data-testid="form-show-occasion" checked={widgetCfg.show_occasion}
                onCheckedChange={(v) => setWidgetCfg({ ...widgetCfg, show_occasion: v })} />
            </div>
          </div>
          <div className="mt-6 border-t border-border/40 pt-5">
            <Label className="text-sm">Custom questions</Label>
            <p className="text-xs text-muted-foreground mt-1 mb-3">Ask diners anything at booking time (e.g. "Window seat?", "First visit?"). Shown on your public page and widget.</p>
            {questions.map((q, i) => (
              <div key={q.id} data-testid={`question-${i}`} className="rounded-xl border border-border/40 p-3 mb-3 space-y-2.5">
                <div className="flex gap-2 items-center">
                  <Input data-testid={`q-label-${i}`} placeholder="Question" value={q.label}
                    onChange={(e) => updateQ(i, { label: e.target.value })} className="bg-background border-border/60 h-9 text-sm" />
                  <button data-testid={`q-remove-${i}`} onClick={() => setQuestions(questions.filter((_, j) => j !== i))}
                    className="text-xs text-muted-foreground hover:text-destructive transition-colors shrink-0">Remove</button>
                </div>
                <div className="flex gap-1.5">
                  {["text", "yesno", "choice"].map((t) => (
                    <button key={t} data-testid={`q-type-${i}-${t}`} onClick={() => updateQ(i, { type: t })}
                      className={`px-2.5 py-1 rounded-full text-[11px] border transition-colors ${q.type === t ? "bg-primary text-primary-foreground border-primary" : "border-border/60 text-muted-foreground hover:text-foreground"}`}>
                      {t === "yesno" ? "Yes / No" : t === "choice" ? "Choice" : "Text"}
                    </button>
                  ))}
                </div>
                {q.type === "choice" && (
                  <Input data-testid={`q-options-${i}`} placeholder="Options, comma separated"
                    value={typeof q.options === "string" ? q.options : (q.options || []).join(", ")}
                    onChange={(e) => updateQ(i, { options: e.target.value })}
                    className="bg-background border-border/60 h-9 text-sm" />
                )}
                <div className="flex items-center gap-2">
                  <Switch data-testid={`q-required-${i}`} checked={q.required} onCheckedChange={(v) => updateQ(i, { required: v })} />
                  <Label className="text-xs">Required</Label>
                </div>
              </div>
            ))}
            <Button data-testid="q-add-btn" size="sm" variant="outline" className="rounded-full h-8"
              onClick={() => setQuestions([...questions, { id: crypto.randomUUID(), label: "", type: "text", options: [], required: false }])}>
              + Add question
            </Button>
          </div>
        </div>
        <div className="rounded-2xl border border-border/60 bg-card p-6" data-testid="regional-card">
          <h3 className="font-outfit font-medium text-lg">Messaging</h3>
          <p className="text-xs text-muted-foreground mt-1">Your timezone (Profile tab above) drives bookable slots, "today" and reminder timing.</p>
          <div className="mt-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm">SMS confirmations &amp; reminders</Label>
                <p className="text-xs text-muted-foreground mt-0.5">Text guests their confirmation and a 24h reminder (uses the platform Twilio account; guests need a phone number).</p>
              </div>
              <Switch data-testid="sms-reminders-switch" checked={smsRem} onCheckedChange={setSmsRem} />
            </div>
            <p className="text-xs text-muted-foreground mt-3" data-testid="sms-fee-hint">
              Using the platform Twilio account adds £0.10 per booking on top of your plan's commission while SMS is on. Add your own Twilio keys below and the fee disappears.
            </p>
            <div className="mt-4 border-t border-border/40 pt-4">
              <div className="flex items-center justify-between">
                <Label className="text-sm">Use our own Twilio account</Label>
                <Switch data-testid="biz-twilio-enabled" checked={twilioCfg.enabled}
                  onCheckedChange={(v) => setTwilioCfg({ ...twilioCfg, enabled: v })} />
              </div>
              {twilioCfg.enabled && (
                <div className="mt-3 space-y-3">
                  <Input data-testid="biz-twilio-sid" placeholder="Account SID (AC...)" value={twilioCfg.account_sid}
                    onChange={(e) => setTwilioCfg({ ...twilioCfg, account_sid: e.target.value })} className="bg-background border-border/60" />
                  <Input data-testid="biz-twilio-token" type="password"
                    placeholder={restaurant.twilio?.has_token ? "Auth token saved — enter to replace" : "Auth token"}
                    value={twilioCfg.auth_token} onChange={(e) => setTwilioCfg({ ...twilioCfg, auth_token: e.target.value })}
                    className="bg-background border-border/60" />
                  <Input data-testid="biz-twilio-from" placeholder="From number (e.g. +447700900000)" value={twilioCfg.from_number}
                    onChange={(e) => setTwilioCfg({ ...twilioCfg, from_number: e.target.value })} className="bg-background border-border/60" />
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-sm">Send via WhatsApp instead of SMS</Label>
                      <p className="text-xs text-muted-foreground mt-0.5">Same Twilio account. Production WhatsApp needs approved message templates; the Twilio sandbox works instantly for testing.</p>
                    </div>
                    <Switch data-testid="biz-twilio-whatsapp" checked={twilioCfg.whatsapp}
                      onCheckedChange={(v) => setTwilioCfg({ ...twilioCfg, whatsapp: v })} />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="rounded-2xl border border-border/60 bg-card p-6" data-testid="branding-card">
          <h3 className="font-outfit font-medium text-lg">Branding</h3>
          <p className="text-xs text-muted-foreground mt-1">
            Logo, header image and gallery on your public page — the logo and header are also used when your page is shared on socials. Upload a file or paste an image URL.
          </p>
          <div className="mt-4 space-y-5">
            {[["Logo", "logo_url", "logo"], ["Header image", "header_image_url", "header"]].map(([label, field, kind]) => (
              <div key={field}>
                <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">{label}</Label>
                <div className="mt-1.5 flex gap-2 items-center">
                  <Input data-testid={`brand-${field}-url`} placeholder="https://… (or upload)" value={brand[field]}
                    onChange={(e) => setBrand({ ...brand, [field]: e.target.value })} className="bg-background border-border/60 h-9 text-sm" />
                  <label data-testid={`brand-${field}-upload`}
                    className="shrink-0 text-xs px-3 py-2 rounded-full border border-border/60 text-muted-foreground hover:text-foreground transition-colors cursor-pointer">
                    {imgUploading === kind ? "Uploading…" : "Upload"}
                    <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden"
                      onChange={(e) => { e.target.files?.[0] && uploadImage(kind, e.target.files[0], (url) => setBrand((b) => ({ ...b, [field]: url }))); e.target.value = ""; }} />
                  </label>
                </div>
                {brand[field] && <img src={brand[field]} alt={label} className="mt-2 h-16 rounded-lg border border-border/40 object-cover" data-testid={`brand-${field}-preview`} />}
              </div>
            ))}
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Gallery photos</Label>
              <div className="mt-1.5 flex gap-2 items-center">
                <Input data-testid="gallery-url-input" placeholder="https://… (or upload)" value={galleryUrl}
                  onChange={(e) => setGalleryUrl(e.target.value)} className="bg-background border-border/60 h-9 text-sm" />
                <Button data-testid="gallery-add-url" size="sm" variant="outline" className="rounded-full shrink-0 h-9"
                  onClick={() => { if (galleryUrl.trim()) { setBrand({ ...brand, photos: [...brand.photos, galleryUrl.trim()] }); setGalleryUrl(""); } }}>
                  Add
                </Button>
                <label data-testid="gallery-upload"
                  className="shrink-0 text-xs px-3 py-2 rounded-full border border-border/60 text-muted-foreground hover:text-foreground transition-colors cursor-pointer">
                  {imgUploading === "gallery" ? "Uploading…" : "Upload"}
                  <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                    onChange={(e) => { e.target.files?.[0] && uploadImage("gallery", e.target.files[0], (url) => setBrand((b) => ({ ...b, photos: [...b.photos, url] }))); e.target.value = ""; }} />
                </label>
              </div>
              {brand.photos.length > 0 && (
                <div className="mt-3 grid grid-cols-3 gap-2">
                  {brand.photos.map((p, i) => (
                    <div key={i} className="relative group">
                      <img src={p} alt="" className="h-20 w-full rounded-lg border border-border/40 object-cover" />
                      <button data-testid={`gallery-remove-${i}`} onClick={() => setBrand({ ...brand, photos: brand.photos.filter((_, j) => j !== i) })}
                        className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/60 text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity">×</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="rounded-2xl border border-border/60 bg-card p-6" data-testid="api-keys-card">
          <h3 className="font-outfit font-medium text-lg">Partner API keys</h3>
          <p className="text-xs text-muted-foreground mt-1">
            Read-only access for POS/payment partners: <span className="font-mono">GET /api/public/{"{key}"}/bookings?date=YYYY-MM-DD</span> and <span className="font-mono">/menus</span>.
          </p>
          <div className="mt-4 rounded-xl border border-border/40 p-4">
            <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Webhook URL (new bookings, cancellations, updates pushed here)</Label>
            <div className="flex gap-2 mt-1.5">
              <Input data-testid="webhook-url-input" type="url" placeholder="https://your-pos.example.com/webhooks/tablediary"
                value={profile.webhook_url} onChange={(e) => setProfile({ ...profile, webhook_url: e.target.value })}
                className="bg-background border-border/60" />
              <Button data-testid="webhook-test-btn" size="sm" variant="outline" className="rounded-full shrink-0"
                onClick={async () => {
                  try {
                    const { data } = await api.post(`/restaurants/${restaurant.id}/webhook-test`);
                    toast.success(`Test sent — ${data.delivery?.status || "delivered"}`);
                  } catch (e) {
                    toast.error(fmtErr(e));
                  }
                }}>
                Send test
              </Button>
            </div>
            {restaurant.webhook_url && restaurant.webhook_last && (
              <span data-testid="webhook-health-badge"
                className={`inline-flex items-center gap-1.5 mt-2 text-[11px] px-2.5 py-1 rounded-full border ${
                  /^HTTP 2/.test(restaurant.webhook_last.status || "")
                    ? "border-emerald-400/40 bg-emerald-400/10 text-emerald-300"
                    : "border-amber-400/40 bg-amber-400/10 text-amber-300"
                }`}>
                <span className={`w-1.5 h-1.5 rounded-full ${/^HTTP 2/.test(restaurant.webhook_last.status || "") ? "bg-emerald-400" : "bg-amber-400"}`} />
                {/^HTTP 2/.test(restaurant.webhook_last.status || "")
                  ? `Healthy — ${restaurant.webhook_stats?.ok || 0} delivered`
                  : `Delivery issues — ${restaurant.webhook_stats?.fail || 0} failed`}
              </span>
            )}
            {restaurant.webhook_last && (
              <p className="text-[11px] text-muted-foreground mt-2" data-testid="webhook-last">
                Last delivery: {restaurant.webhook_last.event} · {restaurant.webhook_last.status} · {(restaurant.webhook_last.at || "").slice(0, 16).replace("T", " ")}
              </p>
            )}
            <p className="text-[11px] text-muted-foreground mt-1.5">Save settings below after changing the URL.</p>
          </div>
          <div className="mt-4 space-y-2.5">
            {apiKeys.map((k) => (
              <div key={k.id} data-testid={`api-key-${k.id}`} className="flex items-center justify-between rounded-xl border border-border/40 px-4 py-3">
                <div>
                  <p className="text-sm font-medium">{k.name}</p>
                  <p className="text-xs text-muted-foreground font-mono">{k.prefix}… · {k.last_used ? `last used ${k.last_used.slice(0, 10)}` : "never used"}</p>
                </div>
                <button data-testid={`api-key-revoke-${k.id}`} onClick={() => deleteKey(k.id)}
                  className="text-xs text-muted-foreground hover:text-destructive transition-colors">Revoke</button>
              </div>
            ))}
            {apiKeys.length === 0 && <p className="text-sm text-muted-foreground" data-testid="no-api-keys-msg">No keys yet.</p>}
          </div>
          {createdKey && (
            <div className="mt-4 rounded-xl border border-emerald-400/40 bg-emerald-400/10 p-4">
              <p className="text-xs text-emerald-500 mb-1.5">Copy this key now — it won't be shown again:</p>
              <p className="text-xs font-mono break-all" data-testid="api-key-created">{createdKey}</p>
              <Button data-testid="api-key-copy" size="sm" variant="outline" className="rounded-full mt-2"
                onClick={() => { navigator.clipboard.writeText(createdKey); toast.success("Copied"); }}>
                Copy key
              </Button>
            </div>
          )}
          <div className="flex gap-2 mt-4">
            <Input data-testid="api-key-name" placeholder="Key name (e.g. Square POS)" value={newKeyName}
              onChange={(e) => setNewKeyName(e.target.value)} className="bg-background border-border/60 h-9 text-sm" />
            <Button data-testid="api-key-create" size="sm" variant="outline" className="rounded-full shrink-0"
              onClick={createKey} disabled={!newKeyName.trim()}>
              Create key
            </Button>
          </div>
        </div>
      </div>

      <Button data-testid="settings-save-btn" onClick={save} disabled={saving} className="lg:col-span-2 rounded-full">
        {saving ? "Saving..." : "Save settings"}
      </Button>
    </div>
  );
}
